import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  collection,
  doc,
  serverTimestamp,
  setDoc,
  updateDoc,
  Timestamp,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { useAuth } from '@/contexts/AuthContext';
import { db } from '@/lib/firebase';
import type { CreateEventInput, ResearchEvent } from '../types/events';
import { loadEvents, saveEvents } from '../mock/events';
import {
  cacheKeyFor,
  loadCachedItems,
  removeCachedItem,
  stripUndefined,
  stripUndefinedForUpdate,
  syncCollectionToCache,
  upsertCachedItem,
} from '../utils/firestoreCache';

// Limite prático abaixo do máximo do Firestore (1 048 487 bytes) para segurança.
const MAX_FIRESTORE_PHOTO_CHARS = 900_000;

/**
 * Mantém fotos base64 pequenas no Firestore (< 900 KB) e remove as grandes.
 * Remove qualquer base64 do localStorage (cota ~5-10 MB por browser).
 */
function cacheable(event: ResearchEvent, forLocalStorage = false): ResearchEvent {
  if (!event.photoUrl.startsWith('data:')) return event;
  if (forLocalStorage || event.photoUrl.length >= MAX_FIRESTORE_PHOTO_CHARS) {
    return { ...event, photoUrl: '' };
  }
  return event;
}

const COLLECTION = 'events';

interface EventsContextValue {
  events: ResearchEvent[];
  getEventById: (id: string) => ResearchEvent | undefined;
  createEvent: (input: CreateEventInput) => ResearchEvent;
  updateEvent: (id: string, patch: Partial<CreateEventInput>) => void;
  updateEventPhoto: (id: string, photoUrl: string) => void;
  /** Persiste participantCount e recordCount derivados dos dados reais (Firestore). */
  syncEventStats: (id: string, participantCount: number, recordCount: number) => void;
  deleteEvent: (id: string) => void;
  canEditEvent: (event: ResearchEvent) => boolean;
}

const EventsContext = createContext<EventsContextValue | null>(null);

function parseEventDoc(
  snap: QueryDocumentSnapshot<DocumentData>
): { id: string; data: ResearchEvent; updatedAt: string; deleted?: boolean } {
  const raw = snap.data();
  const updatedAt =
    raw.updatedAt instanceof Timestamp
      ? raw.updatedAt.toDate().toISOString()
      : new Date().toISOString();

  return {
    id: snap.id,
    updatedAt,
    deleted: Boolean(raw.deleted),
    data: {
      id: snap.id,
      title: raw.title,
      photoUrl: raw.photoUrl ?? '',
      school: raw.school,
      type: raw.type,
      customTypeLabel: raw.customTypeLabel,
      startDate: raw.startDate,
      startTime: raw.startTime ?? '08:00',
      endDate: raw.endDate,
      endTime: raw.endTime ?? '17:00',
      createdBy: raw.createdBy,
      collaboratorEmails: raw.collaboratorEmails ?? [],
      participantCount: raw.participantCount ?? 0,
      recordCount: raw.recordCount ?? 0,
      createdAt: raw.createdAt ?? updatedAt,
      lastSyncAt: raw.lastSyncAt ?? updatedAt,
    },
  };
}

export function EventsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const email = user?.email ?? '';
  const useFirestore = Boolean(db && user);
  const cacheKey = user ? cacheKeyFor(COLLECTION, user.uid) : '';

  const [events, setEvents] = useState<ResearchEvent[]>(() =>
    useFirestore ? loadCachedItems<ResearchEvent>(cacheKey) : loadEvents()
  );

  useEffect(() => {
    if (!useFirestore || !db || !user) return;
    // Mostra o que já está em cache imediatamente, depois sincroniza
    // só os documentos do Firestore criados/alterados desde o último acesso —
    // a coleção é compartilhada por toda a equipe, sem filtro por usuário.
    setEvents(loadCachedItems<ResearchEvent>(cacheKey));
    syncCollectionToCache<ResearchEvent>({
      db,
      cacheKey,
      collectionName: COLLECTION,
      parseDoc: parseEventDoc,
    })
      .then(setEvents)
      .catch((err) => console.error('Falha ao sincronizar eventos com Firestore', err));
  }, [useFirestore, cacheKey, user]);

  const persistMock = useCallback((next: ResearchEvent[]) => {
    setEvents(next);
    saveEvents(next);
  }, []);

  const getEventById = useCallback(
    (id: string) => events.find((e) => e.id === id),
    [events]
  );

  // Dashboard de equipe: qualquer usuário autenticado tem acesso total.
  const canEditEvent = useCallback((_event: ResearchEvent) => Boolean(email), [email]);

  const createEvent = useCallback(
    (input: CreateEventInput): ResearchEvent => {
      const now = new Date().toISOString();
      const ownerEmail = email || 'pesquisador@conectar.app';
      const base: ResearchEvent = {
        id: '',
        title: input.title,
        photoUrl: input.photoUrl || '',
        school: input.school,
        type: input.type,
        customTypeLabel: input.customTypeLabel,
        noPeriod: input.noPeriod ?? false,
        startDate: input.startDate,
        startTime: input.startTime,
        endDate: input.endDate,
        endTime: input.endTime,
        createdBy: ownerEmail,
        collaboratorEmails: input.collaboratorEmails.length
          ? input.collaboratorEmails
          : [ownerEmail],
        participantCount: 0,
        recordCount: 0,
        createdAt: now,
        lastSyncAt: now,
      };

      if (useFirestore && db) {
        const ref = doc(collection(db, COLLECTION));
        const event: ResearchEvent = { ...base, id: ref.id };
        setEvents((prev) => [event, ...prev]);
        const safeEvent = cacheable(event);          // ok para Firestore (só remove se > 900KB)
        if (safeEvent.photoUrl === '' && event.photoUrl !== '') {
          console.warn(`Foto do evento ${event.id} excede ${MAX_FIRESTORE_PHOTO_CHARS} caracteres mesmo após compressão — não foi salva no Firestore.`);
        }
        upsertCachedItem(cacheKey, event.id, cacheable(event, true)); // localStorage: sempre remove base64
        setDoc(ref, stripUndefined({ ...safeEvent, updatedAt: serverTimestamp() })).catch((err) =>
          console.error('Falha ao criar evento no Firestore', err)
        );
        return event;
      }

      const event: ResearchEvent = { ...base, id: `evt-${Date.now()}` };
      persistMock([event, ...events]);
      return event;
    },
    [email, events, persistMock, useFirestore, cacheKey]
  );

  const updateEvent = useCallback(
    (id: string, patch: Partial<CreateEventInput>) => {
      const current = events.find((e) => e.id === id);
      if (!current) return;
      const updated: ResearchEvent = { ...current, ...patch };

      setEvents((prev) => prev.map((e) => (e.id === id ? updated : e)));

      if (useFirestore && db) {
        upsertCachedItem(cacheKey, id, cacheable(updated, true)); // localStorage
        const oversizedPhoto = patch.photoUrl?.startsWith('data:') && patch.photoUrl.length >= MAX_FIRESTORE_PHOTO_CHARS;
        if (oversizedPhoto) {
          console.warn(`Foto do evento ${id} excede ${MAX_FIRESTORE_PHOTO_CHARS} caracteres mesmo após compressão — não foi salva no Firestore.`);
        }
        const safePatch = oversizedPhoto ? { ...patch, photoUrl: '' } : patch;
        updateDoc(
          doc(db, COLLECTION, id),
          stripUndefinedForUpdate({ ...safePatch, updatedAt: serverTimestamp() })
        ).catch((err) => console.error('Falha ao atualizar evento no Firestore', err));
        return;
      }

      persistMock(events.map((e) => (e.id === id ? updated : e)));
    },
    [events, persistMock, useFirestore, cacheKey]
  );

  const updateEventPhoto = useCallback(
    (id: string, photoUrl: string) => updateEvent(id, { photoUrl }),
    [updateEvent]
  );

  const syncEventStats = useCallback(
    (id: string, participantCount: number, recordCount: number) => {
      const current = events.find((e) => e.id === id);
      if (!current) return;
      const updated = { ...current, participantCount, recordCount };
      setEvents((prev) => prev.map((e) => (e.id === id ? updated : e)));
      if (useFirestore && db) {
        upsertCachedItem(cacheKey, id, cacheable(updated));
        updateDoc(doc(db, COLLECTION, id), { participantCount, recordCount, updatedAt: serverTimestamp() }).catch(
          (err) => console.error('Falha ao sincronizar stats do evento no Firestore', err)
        );
      }
    },
    [events, useFirestore, cacheKey]
  );

  const deleteEvent = useCallback(
    (id: string) => {
      setEvents((prev) => prev.filter((e) => e.id !== id));

      if (useFirestore && db) {
        removeCachedItem(cacheKey, id);
        updateDoc(doc(db, COLLECTION, id), {
          deleted: true,
          updatedAt: serverTimestamp(),
        }).catch((err) => console.error('Falha ao excluir evento no Firestore', err));
        return;
      }

      persistMock(events.filter((e) => e.id !== id));
    },
    [events, persistMock, useFirestore, cacheKey]
  );

  const value = useMemo(
    () => ({
      events,
      getEventById,
      createEvent,
      updateEvent,
      updateEventPhoto,
      syncEventStats,
      deleteEvent,
      canEditEvent,
    }),
    [events, getEventById, createEvent, updateEvent, updateEventPhoto, syncEventStats, deleteEvent, canEditEvent]
  );

  return (
    <EventsContext.Provider value={value}>{children}</EventsContext.Provider>
  );
}

export function useEvents() {
  const ctx = useContext(EventsContext);
  if (!ctx) {
    throw new Error('useEvents deve ser usado dentro de EventsProvider');
  }
  return ctx;
}
